import React from "react";

import SeloVerificado from "./SeloVerificado";
import { fmtTelefone, linkWhatsapp } from "./perfilEmbarcador";

// Bloco "Quem está contratando" (épico PO1-3527): o que a transportadora de
// fora da carteira vê sobre o embarcador no leilão (página pública), no BID
// (MeuFrete) e na prévia da tela de Perfil. SEM regra de visibilidade: o
// backend (gobid-perfil) já manda só o que o embarcador autorizou; campo
// ausente é campo que não aparece. `perfil` nulo não desenha nada.
//
// Estilos inline por token (as mesmas variáveis de Public/LeilaoFrete), para
// funcionar na página pública, que não carrega o styles.css do Leilão.

const BOX_STYLE = {
  background: "var(--everlog-card-surface-section)",
  border: "1px solid var(--everlog-card-border-soft)",
  borderRadius: 8,
  padding: "14px 16px",
  marginBottom: 20,
  color: "var(--everlog-card-text)",
};
const EYEBROW_STYLE = { fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: "var(--everlog-card-muted)", marginBottom: 6 };
const NOME_STYLE = { fontSize: 17, fontWeight: 700, color: "var(--everlog-card-title)", display: "flex", alignItems: "center", flexWrap: "wrap" };
const MUTED_STYLE = { color: "var(--everlog-card-muted)", fontSize: 12 };
const LABEL_STYLE = { fontSize: 11, color: "var(--everlog-card-muted)", marginBottom: 2 };
const VALUE_STYLE = { fontWeight: 600, color: "var(--everlog-card-title)" };
const TEXTO_STYLE = { marginTop: 10, whiteSpace: "pre-line", lineHeight: 1.5 };
const CHIPS_STYLE = { display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 };
const CHIP_STYLE = {
  display: "inline-block",
  padding: "3px 10px",
  borderRadius: 999,
  fontSize: 12,
  border: "1px solid var(--everlog-card-border-soft)",
  background: "var(--everlog-card-surface-elevated)",
  color: "var(--everlog-card-title)",
};
const GRID_STYLE = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "10px 16px", marginTop: 12 };
const SECAO_STYLE = { marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--everlog-card-divider, var(--everlog-card-border-soft))" };
const LINK_STYLE = { color: "var(--theme-accent)", textDecoration: "none", fontWeight: 600 };
const TABELA_STYLE = { width: "100%", borderCollapse: "collapse", marginTop: 6, fontSize: 13 };
const TH_STYLE = { textAlign: "left", fontSize: 11, fontWeight: 700, color: "var(--everlog-card-muted)", padding: "4px 6px", borderBottom: "1px solid var(--everlog-card-border-soft)" };
const TD_STYLE = { padding: "4px 6px", color: "var(--everlog-card-title)", borderBottom: "1px solid var(--everlog-card-border-soft)" };
const NOTA_STYLE = { marginTop: 12, padding: "10px 12px", borderRadius: 8, background: "var(--everlog-card-surface-elevated)", border: "1px solid var(--everlog-card-border-soft)", fontSize: 13, lineHeight: 1.5 };

function Chip({ children }) {
  return <span style={CHIP_STYLE}>{children}</span>;
}

export default function QuemEstaContratando({ perfil, style, titulo = "Quem está contratando" }) {
  if (!perfil) return null;
  const contato = perfil.contato || {};
  const exigencias = Array.isArray(perfil.exigencias) ? perfil.exigencias.filter((e) => e && e.texto) : [];
  const cidades = Array.isArray(perfil.cidadesOrigem) ? perfil.cidadesOrigem : [];
  const indicadores = Array.isArray(perfil.indicadores) ? perfil.indicadores : [];
  const temChips = perfil.segmento || perfil.tipoCarga || perfil.prazoPagamento;
  const temContato = contato.nome || contato.email || contato.telefone || contato.whatsapp;
  const whatsappUrl = contato.whatsapp ? contato.whatsappUrl || linkWhatsapp(contato.whatsapp) : null;

  return (
    <div style={{ ...BOX_STYLE, ...style }} data-testid="quem-esta-contratando">
      <div style={EYEBROW_STYLE}>{titulo}</div>
      <div style={NOME_STYLE}>
        <span>{perfil.nome || perfil.razaoSocial || "Embarcador"}</span>
        <SeloVerificado verificado={perfil.verificado} />
      </div>
      {perfil.razaoSocial && perfil.razaoSocial !== perfil.nome ? <div style={MUTED_STYLE}>{perfil.razaoSocial}</div> : null}
      {perfil.cnpj ? <div style={MUTED_STYLE}>CNPJ {perfil.cnpj}</div> : null}

      {perfil.apresentacao ? <div style={TEXTO_STYLE}>{perfil.apresentacao}</div> : null}

      {temChips ? (
        <div style={CHIPS_STYLE}>
          {perfil.segmento ? <Chip>{perfil.segmento}</Chip> : null}
          {perfil.tipoCarga ? <Chip>Carga: {perfil.tipoCarga}</Chip> : null}
          {perfil.prazoPagamento ? <Chip>Pagamento: {perfil.prazoPagamento}</Chip> : null}
        </div>
      ) : null}

      {exigencias.length ? (
        <div style={SECAO_STYLE}>
          <div style={EYEBROW_STYLE}>Como opera</div>
          <div style={GRID_STYLE}>
            {exigencias.map((e) => (
              <div key={e.rotulo}>
                <div style={LABEL_STYLE}>{e.rotulo}</div>
                <div style={VALUE_STYLE}>{e.texto}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {cidades.length ? (
        <div style={SECAO_STYLE}>
          <div style={EYEBROW_STYLE}>Cidades de origem</div>
          <div style={CHIPS_STYLE}>
            {cidades.map((c) => (
              <Chip key={c}>{c}</Chip>
            ))}
          </div>
        </div>
      ) : null}

      {indicadores.length ? (
        <div style={SECAO_STYLE}>
          <div style={EYEBROW_STYLE}>Volume por UF de origem</div>
          <table style={TABELA_STYLE} data-testid="qec-indicadores">
            <thead>
              <tr>
                <th style={TH_STYLE}>UF</th>
                <th style={TH_STYLE}>Embarques/mês</th>
                <th style={TH_STYLE}>Recorrência</th>
              </tr>
            </thead>
            <tbody>
              {indicadores.map((i) => (
                <tr key={i.uf}>
                  <td style={TD_STYLE}>{i.uf}</td>
                  <td style={TD_STYLE}>{i.faixa}</td>
                  <td style={TD_STYLE}>{i.recorrencia}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ ...MUTED_STYLE, marginTop: 4 }}>Faixas calculadas sobre os últimos 6 meses completos.</div>
        </div>
      ) : null}

      {temContato ? (
        <div style={SECAO_STYLE}>
          <div style={EYEBROW_STYLE}>Contato</div>
          <div style={GRID_STYLE}>
            {contato.nome ? (
              <div>
                <div style={LABEL_STYLE}>Nome</div>
                <div style={VALUE_STYLE}>{contato.nome}</div>
              </div>
            ) : null}
            {contato.email ? (
              <div>
                <div style={LABEL_STYLE}>E-mail</div>
                <div style={VALUE_STYLE}>
                  <a href={`mailto:${contato.email}`} style={LINK_STYLE}>
                    {contato.email}
                  </a>
                </div>
              </div>
            ) : null}
            {contato.telefone ? (
              <div>
                <div style={LABEL_STYLE}>Telefone</div>
                <div style={VALUE_STYLE}>{fmtTelefone(contato.telefone)}</div>
              </div>
            ) : null}
            {contato.whatsapp ? (
              <div>
                <div style={LABEL_STYLE}>WhatsApp</div>
                <div style={VALUE_STYLE}>
                  <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" style={LINK_STYLE} data-testid="qec-whatsapp">
                    {fmtTelefone(contato.whatsapp)}
                  </a>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {perfil.comoFunciona || perfil.seVoceGanhar ? (
        <div style={NOTA_STYLE}>
          {perfil.comoFunciona ? <div>{perfil.comoFunciona}</div> : null}
          {perfil.seVoceGanhar ? <div style={{ marginTop: perfil.comoFunciona ? 8 : 0, fontWeight: 600, color: "var(--everlog-card-title)" }}>{perfil.seVoceGanhar}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
